<template>
  <div class="container py-3">
    <h2 class="fs-4 fw-bold mb-4 text-center">Job Details</h2>

    <!-- Loading / Error -->
    <div v-if="loading" class="alert alert-info text-center">Loading booking...</div>
    <div v-else-if="!booking" class="alert alert-danger text-center">Booking not found or unauthorized</div>

    <!-- Main Card -->
    <div v-else class="card shadow-sm border-0 rounded-3 p-4">
      <div class="card-body">
        <!-- Basic Details -->
        <div class="row g-3">
          
          
          <div class="col-md-6">
            <label class="form-label fw-semibold">Booking ID</label>
            <input type="text" class="form-control" :value="booking.id" disabled />
          </div>
          
          
          <div class="col-md-6">
            <label class="form-label fw-semibold">Current Status</label>
            <input type="text" class="form-control" :value="booking.status || 'New'" disabled />
          </div>
          
          <div class="col-md-6">
            <label class="form-label fw-semibold">Agent Created</label>
            <input type="text" class="form-control" :value="getClientName(booking.clientId)" disabled />
          </div>

          <!-- Vessel Name -->
          <div class="col-md-6">
            <label class="form-label fw-semibold">Vessel Name</label>
            <input
              type="text"
              class="form-control"
              v-model="form.vesselName"
              :disabled="!isClient"
            />
          </div>

          
          <div class="col-md-6">
            <label class="form-label fw-semibold">Start Date</label>
            <input
              type="date"
              class="form-control"
              v-model="form.startDate"
              :disabled="!isClient"
            />
          </div>

          <div class="col-md-6">
            <label for="startTime" class="form-label fw-semibold">Start Time</label>
            <div class="form-control p-0">
              <VueTimepicker
                v-model="form.startTime"
                format="HH:mm"
                :hour-range="Array.from({ length: 24 }, (_, i) => i)"
                :minute-range="Array.from({ length: 60 }, (_, i) => i)"
                manual-input
                class="vue__time-picker border-0 w-100"
                :disabled="!isClient"
              />
            </div>
          </div>

          <div class="col-12">
            <label class="form-label fw-semibold">From</label>
            <input
              type="text"
              class="form-control"
              v-model="form.startLocation"
              :disabled="!isClient"
            />
          </div>

          <!-- Pax -->
          <div class="col-md-6">
            <label class="form-label fw-semibold">Pax</label>
            <input
              type="number"
              class="form-control"
              v-model="form.pax"
              :disabled="!isClient"
            />
          </div>

          <!-- Flight Numbers (Arrival / Departure) -->
          <div class="col-12 mt-3">
            <label class="form-label fw-semibold">Flight Numbers</label>

            <div
              v-for="(f, i) in form.flightNumbers"
              :key="i"
              class="d-flex gap-3 mb-2 align-items-center flex-wrap"
            >
              <input
                v-model="f.flight"
                class="form-control"
                placeholder="e.g., SQ316"
                :disabled="!isClient"
                style="flex: 1;"
              />

              <!-- Arrival -->
              <div class="form-check d-flex align-items-center">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="f.isArrival"
                  @change="handleArrivalChange(i)"
                  :disabled="!isClient"
                />
                <label class="form-check-label small ms-1">Arrival</label>
              </div>

              <!-- Departure -->
              <div class="form-check d-flex align-items-center">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="f.isDeparture"
                  @change="handleDepartureChange(i)"
                  :disabled="!isClient"
                />
                <label class="form-check-label small ms-1">Departure</label>
              </div>

              <button
                v-if="isClient"
                type="button"
                class="btn btn-danger btn-sm"
                @click="removeFlight(i)"
              >
                Remove
              </button>
            </div>

            <button
              v-if="isClient"
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="addFlight"
            >
              + Add Flight
            </button>
          </div>
          
          <!-- Counter Number -->
          <div class="col-md-6">
            <label class="form-label fw-semibold">Counter Number</label>
            <select v-model="form.counterNumber" class="form-select" :disabled="!isClient">
              <option disabled value="">Select counter</option>
              <option value= "NA">N/A</option>
              <option v-for="n in 16" :key="n" :value="`Counter ${n}`">Counter {{ n }}</option>
            </select>
          </div>

          <!-- Trips -->
          <div class="col-12 mt-3">
            <label class="form-label fw-semibold">Trips</label>
            <div v-for="(t, i) in form.trips" :key="i" class="d-flex gap-2 mb-2">
              <input
                v-model="form.trips[i]"
                class="form-control"
                placeholder="e.g., Hotel to Airport"
                :disabled="!isClient"
              />
              <button
                type="button"
                class="btn btn-danger btn-sm"
                @click="removeTrip(i)"
                v-if="isClient"
              >
                Remove
              </button>
            </div>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="addTrip"
              v-if="isClient"
            >
              + Add Trip
            </button>
          </div>


          <!-- End Location -->
          <div class="col-12">
            <label class="form-label fw-semibold">To</label>
            <input
              type="text"
              class="form-control"
              v-model="form.endLocation"
              :disabled="!isClient"
            />
          </div>


          <div
            v-for="(c, i) in form.pocContact"
            :key="i"
            class="d-flex gap-2 mb-2 align-items-center flex-wrap"
          >
            <div class="input-group flex-grow-1">
              <input
                v-model="form.pocContact[i]"
                class="form-control"
                placeholder="Enter contact number (+65...)"
                :disabled="!isClient"
              />

              <!-- ✅ WhatsApp link -->
              <a
                v-if="form.pocContact[i]"
                :href="`https://wa.me/${form.pocContact[i].replace(/[^0-9]/g, '')}`"
                target="_blank"
                class="btn btn-success d-flex align-items-center justify-content-center"
                title="Open in WhatsApp"
                style="min-width: 42px;"
              >
                <MessageCircle size="18" stroke-width="2" />
              </a>
            </div>

            <button
              v-if="isClient"
              type="button"
              class="btn btn-danger btn-sm"
              @click="removeContact(i)"
            >
              Remove
            </button>
          </div>

          <button
            v-if="isClient"
            type="button"
            class="btn btn-outline-primary btn-sm"
            @click="addContact"
          >
            + Add Contact
          </button>
      
        </div>

        <hr class="my-4" />

        <!-- Status Update -->
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label fw-semibold">Update Status</label>
              <select v-model="form.status" class="form-select" disabled>     
              <!-- <select v-model="form.status" class="form-select" :disabled="isClient">        -->
              <option disabled value="">Select status</option>
              <option v-for="s in statusOptions" :key="s" :value="s">{{ s }}</option>
            </select>
            <small class="text-muted">Allowed next: {{ allowedNext.join(', ') || '—' }}</small>
          </div>

          <div class="col-md-6">
            <label class="form-label fw-semibold">Remarks</label>
            <input v-model="form.remarks" type="text" class="form-control" placeholder="Optional remark" />
            <small class="text-muted"> {{ prevRemarks }}</small>
          </div>
          <!-- Crew Name -->
          <div class="col-md-6">
            <label class="form-label fw-semibold">Crew Name</label>
            <input
              type="text"
              class="form-control"
              v-model="form.crewName"
            />
          </div>
        </div>


        <!-- Assign Drivers / Outsource (Admin only) -->
        <div class="mt-4" v-if="isAdmin">
          <label class="form-label fw-semibold">Assign Drivers / Outsource</label>

          <div v-for="(driverId, i) in form.driverIDs" :key="i" class="d-flex gap-2 mb-2 align-items-center">
            <template v-if="driverId === 'OUTSOURCE'">
              <input type="text" class="form-control bg-light" value="Outsource" readonly />
            </template>
            <template v-else>
              <select v-model="form.driverIDs[i]" class="form-select">
                <option disabled value="">Select driver</option>
                <option v-for="drv in drivers" :key="drv.id" :value="drv.id">
                  {{ drv.name }} 
                </option>
              </select>
            </template>
            <button type="button" class="btn btn-danger btn-sm" @click="removeDriver(i)">Remove</button>
          </div>

          <div class="d-flex gap-2 mt-2">
            <button type="button" class="btn btn-outline-primary btn-sm" @click="addDriver">+ Add Driver</button>
            <button type="button" class="btn btn-outline-danger btn-sm" @click="addOutsource">+ Add Outsource</button>
          </div>
        </div>

        <!-- File Upload -->
        <div class="mt-4">
          <label class="form-label fw-semibold">Upload Documents</label>
          <div v-for="(f, i) in uploadedFiles" :key="i" class="d-flex gap-2 mb-2">
            <input type="file" class="form-control" @change="onFileChange($event, i)" />
            <button type="button" class="btn btn-danger btn-sm" @click="removeFile(i)">Remove</button>
          </div>
          <button type="button" class="btn btn-outline-primary btn-sm" @click="addFile">+ Add File</button>
        </div>
        <!-- Vouchers Section -->
        <div class="mt-4" v-if="isAdmin">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h5 class="fw-bold mb-0">Transport Vouchers</h5>
            <button
              v-if="booking"
              class="btn btn-outline-primary btn-sm"
              @click="goToSignDoc"
            >
              + Add Voucher
            </button>
          </div>

          <div v-if="booking.voucherIDs && booking.voucherIDs.length" class="space-y-2">
            <div
              v-for="(voucher, i) in booking.voucherIDs"
              :key="i"
              class="d-flex justify-content-between align-items-center p-3 bg-light rounded border mb-2"
            >
              <div class="fw-semibold">
                TransportVoucher-{{ i + 1 }}
                <span class="text-muted small ms-1">({{ voucher }})</span>
              </div>

              <div class="d-flex gap-2">
                <button class="btn btn-link text-primary p-0" @click="handleEditVoucher(voucher)">
                  Edit
                </button>
                <!-- <button class="btn btn-link text-danger p-0" @click="deleteVoucher(voucher)">
                  Delete
                </button> -->
              </div>
            </div>
          </div>

          <p v-else class="text-muted">No vouchers yet</p>
        </div>

        <!-- Existing Documents -->
        <div class="mt-4">
          <!-- <div class="d-flex align-items-center justify-content-between mb-2">
            <h5 class="fw-bold mb-0">Existing Documents</h5>
            <a
              v-if="this.$route.params.id"
              :href="canViewVoucher
                ? `https://amdtransportatio.com.sg/SignDoc/${this.$route.params.id}/${this.$route.params.token}`
                : null"
              :class="[
                'fw-semibold text-decoration-none',
                canViewVoucher ? 'text-primary' : 'text-muted pe-none'
              ]"
              :title="canViewVoucher
                ? 'Open Transport Voucher'
                : 'Voucher not available until job progresses'"
              :style="{ cursor: canViewVoucher ? 'pointer' : 'not-allowed' }"
              :tabindex="canViewVoucher ? 0 : -1"
            >
              Transport Voucher
              <i class="bi bi-box-arrow-up-right ms-1"></i>
            </a>

          </div> -->
          
          <ul class="list-unstyled">
            <li v-for="(doc, idx) in booking.documents" :key="idx" class="mb-1">
              <a href="#" @click.prevent="downloadDocument(booking.id, doc.filePath)">📎 {{ doc.fileName }}</a>
              <span class="text-muted ms-2">{{ doc.uploadedAt || '' }}</span>
            </li>
          </ul>
          <p v-if="!booking.documents || booking.documents.length === 0" class="text-muted">No documents uploaded.</p>
        </div>

        <!-- Buttons -->
        <div class="mt-4 d-flex gap-2">
          <!-- <button class="btn btn-outline-secondary" @click="goBack">Back</button> -->
          <button class="btn btn-primary flex-fill" :disabled="loadingSave || !canSave" @click="saveAll">
            <span v-if="!loadingSave">Save Changes</span>
            <span v-else class="spinner-border spinner-border-sm"></span>
          </button>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import axios from 'axios'
import router from '@/router'
import VueTimepicker from 'vue3-timepicker'
import 'vue3-timepicker/dist/VueTimepicker.css'
import { useToast } from 'vue-toastification'
import { MessageCircle } from 'lucide-vue-next'

export default {
  name: 'JobStartEndPage',
  components: { VueTimepicker,MessageCircle  },

  data() {
    return {
      // $apiURL: 'http://127.0.0.1:5001',
      // $apiURL: "https://amdtransportation-be-main-build-159134604691.us-central1.run.app",

      loading: true,
      loadingSave: false,
      booking: null,
      role: 'client',
      drivers: [],
      clients: [],
      // form: { status: '', remark: '', driverIDs: [], flightNumbers: [''] },
      uploadedFiles: [{ file: null }],
      statusOptions: ['New', 'Assigned', 'On Route', 'Arrived', 'On Board', 'Completed',"Assigning", "Cancelled"],

      form: { status: '', 
      remarks: '', 
      driverIDs: [], 
      flightNumbers: [''], 
      startTime: '', 
      startLocation: '', 
      endLocation: '', 
      pax: '', vesselName: '', counterNumber: '',  crewName: '',   startDate: '',},
      prevRemarks:"",
   
    }
  },

  computed: {
    isAdmin() { return this.role === 'admin' || this.role === 'driver' },
    isDriver() { return this.role === 'driver' },
    isClient() { return this.role === 'client' },

    canStatusUpdate() { return this.isAdmin || this.isDriver },
    allowedNext() {
      const current = this.booking?.status || 'New'
      const map = {
        New: ['Assigned'],
        Assigned: ['On Route', 'Failure'],
        'On Route': ['Arrived', 'Failure'],
        Arrived: ['POB', 'Failure'],
        POB: ['Completed', 'Failure'],
        Completed: [],
        Failure: []
      }
      return map[current] || []
    },
    canSave() {
      return true
    },
    canViewVoucher() {
      const allowed = ['On_Board',"On Board","Arrived", 'DOP', 'Completed', 'Cancelled']
      return this.booking && allowed.includes(this.booking.status)
  },
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  methods: {
    getAuthHeader() {
      const token = this.$route.params.token
      if (!token) {
        this.toast.error('Missing access token in URL')
        throw new Error('Missing access token')
      }
      return { Authorization: token }
    },
     goToSignDoc() {
        const id = this.$route.params.id
        const token = this.$route.params.token
        const url = `https://amdtransportation.com.sg/SignDoc/${id}/${token}`
        window.location.href = url
        // window.open(url, '_blank') // open in new tab
        // or use window.location.href = url if you want same tab
      },
    normalizeDateToInput(value) {
      if (!value) return ''

      // Firestore timestamp support just in case
      if (value?.seconds) {
        const dt = new Date(value.seconds * 1000)
        return this.formatDateForInput(dt)
      }

      if (value instanceof Date) {
        return this.formatDateForInput(value)
      }

      const str = String(value).trim()

      // ✅ ISO datetime: 2026-05-11T08:00:00+08:00
      if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
        const dt = new Date(str)
        return this.formatDateForInput(dt)
      }

      // ✅ Already date input format: 2026-05-11
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        return str
      }

      // ✅ DD/MM/YYYY or DD-MM-YYYY: 11/05/2026
      const dmy = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
      if (dmy) {
        const dd = String(dmy[1]).padStart(2, '0')
        const mm = String(dmy[2]).padStart(2, '0')
        const yyyy = dmy[3]
        return `${yyyy}-${mm}-${dd}`
      }

      return ''
    },

    formatDateForInput(dateObj) {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Singapore',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(dateObj)

      const yyyy = parts.find(p => p.type === 'year')?.value
      const mm = parts.find(p => p.type === 'month')?.value
      const dd = parts.find(p => p.type === 'day')?.value

      return `${yyyy}-${mm}-${dd}`
    },

    normalizeTimeToInput(value) {
      if (!value) return ''

      if (value?.seconds) {
        const dt = new Date(value.seconds * 1000)
        return this.formatTimeForInput(dt)
      }

      const str = String(value).trim()

      // ✅ ISO datetime
      if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
        return this.formatTimeForInput(new Date(str))
      }

      // ✅ HH:mm
      if (/^\d{2}:\d{2}$/.test(str)) {
        return str
      }

      // ✅ HH:mm:ss
      if (/^\d{2}:\d{2}:\d{2}$/.test(str)) {
        return str.slice(0, 5)
      }

      return ''
    },

    formatTimeForInput(dateObj) {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Singapore',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(dateObj)

      const hh = parts.find(p => p.type === 'hour')?.value
      const min = parts.find(p => p.type === 'minute')?.value

      return `${hh}:${min}`
    },
    async fetchBooking() {
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.get(`${this.$apiURL}/api/bookings/${this.$route.params.id}`, { headers })
        if (!res.data?.booking) {
          this.toast.error('Booking not found')
          return
        }

        this.booking = res.data.booking
        this.role = localStorage.getItem('userRole') || 'client'
        if(this.booking.status=="DOP"){this.booking.status = "Completed"}
        if (this.booking.status === 'Failure') {
            this.booking.status = 'Cancelled'  // ✅ display as Cancelled
          }

        this.form = {
          status: this.booking.status || 'New',
          remarks: '',
          driverIDs: this.booking.driverIDs || [],
          startTime: this.booking.startTime ? this.booking.startTime.slice(0, 16) : '',
          startLocation: this.booking.rawStartLocation || '',
          endLocation: this.booking.rawEndLocation || '',
          pax: this.booking.pax || '',
          vesselName: this.booking.vesselName || '',
          counterNumber: this.booking.counterNumber || '',
          pocContact: this.booking.pocContact || '',
          flightNumbers: [],
          trips: [],
          crewName: this.booking.crewName || '',
        }
        const rawStartTime = this.booking.startTime || this.booking.startDate || ''

        this.form.startDate = this.normalizeDateToInput(rawStartTime)
        this.form.startTime = this.normalizeTimeToInput(this.booking.startTime || '')


        this.prevRemarks = this.booking.remarks || ''

        this.form.flightNumbers = Array.isArray(this.booking.flightNumbers)
        ? this.booking.flightNumbers.map(f => ({
            flight: f.flight || f.number || '',
            isArrival: !!f.isArrival,
            isDeparture: !!f.isDeparture
          }))
        : [{ flight: '', isArrival: false, isDeparture: false }]

        // 🔹 trips = ["...", "..."]
        if (Array.isArray(this.booking.trips)) {
          this.form.trips = [...this.booking.trips]
        } else if (typeof this.booking.trips === 'string' && this.booking.trips.startsWith('[')) {
          try {
            this.form.trips = JSON.parse(this.booking.trips)
          } catch {
            this.form.trips = [this.booking.trips]
          }
        } else {
          this.form.trips = ['']
        }

      } catch (err) {
        console.error('❌ fetchBooking error:', err)
        this.toast?.error('Error loading booking details')
      } finally {
        this.loading = false
      }
    },
  async fetchDrivers() {
    try {
      const headers = await this.getAuthHeader()
      const res = await axios.get(`${this.$apiURL}/drivers`, { headers })
      if (res.data?.drivers) {
        this.drivers = res.data.drivers
      } else {
        this.drivers = []
      }
    } catch (err) {
      console.error('❌ fetchDrivers error:', err)
      this.toast?.error('Unable to load drivers list')
      this.drivers = []
    }
    },
    fmtDT(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })
      } catch { return iso }
    },
    goBack() { router.back() },
    addDriver() { this.form.driverIDs.push('') },
    addOutsource() { if (!this.form.driverIDs.includes('OUTSOURCE')) this.form.driverIDs.push('OUTSOURCE') },
    removeDriver(i) { this.form.driverIDs.splice(i, 1) },
    addFile() { this.uploadedFiles.push({ file: null }) },
    removeFile(i) { this.uploadedFiles.splice(i, 1) },
    onFileChange(e, i) {
      const f = e.target.files[0]
      if (f) this.uploadedFiles[i].file = f
    },
    // ✅ Crew contacts
    addContact() {
      if (!Array.isArray(this.form.pocContact)) this.form.pocContact = []
      this.form.pocContact.push('')
    },
    removeContact(i) {
      this.form.pocContact.splice(i, 1)
    },

    // ✅ Flight numbers
    addFlight() {
      this.form.flightNumbers.push({ flight: '', isArrival: false, isDeparture: false })
    },
    removeFlight(i) {
      this.form.flightNumbers.splice(i, 1)
    },
    handleArrivalChange(i) {
      if (this.form.flightNumbers[i].isArrival) {
        this.form.flightNumbers[i].isDeparture = false
      }
    },
    handleDepartureChange(i) {
      if (this.form.flightNumbers[i].isDeparture) {
        this.form.flightNumbers[i].isArrival = false
      }
    },

    addTrip() { this.form.trips.push('') },
    removeTrip(i) { this.form.trips.splice(i, 1) },


    async downloadDocument(bookingId, filePath) {
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.get(`${this.$apiURL}/api/bookings/${bookingId}/document`, {
          headers, responseType: 'blob', params: { filePath }
        })
        const filename = filePath.split('/').pop()
        const blob = new Blob([res.data])
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        console.error('❌ downloadDocument error:', err)
      }
    },
    async handleEditVoucher(voucherID) {
      try {
        // make sure got token
        const token = this.$route.params.token
        if (!token) {
          this.toast.error('Missing access token')
          return
        }

        // push to the voucher edit page (replace with your actual route)
        const url = `https://amdtransportation.com.sg/EditSignDoc/${voucherID}/${token}`
        window.location.href = url
      } catch (err) {
        console.error('❌ handleEditVoucher error:', err)
        this.toast.error('Failed to open voucher page')
      }
    },
    // Voucher edit + delete only
    // async handleEditVoucher(voucherID) {
    //   try {
    //     const headers = await this.getAuthHeader()
    //     const res = await axios.get(`${this.$apiURL}/api/vouchers/${voucherID}`, { headers })
    //     if (!res.data?.voucher) {
    //       this.toast.error('Voucher not found')
    //       return
    //     }

    //     const existing = res.data.voucher
    //     const newRemarks = prompt('Enter updated remarks:', existing.remarks || '')
    //     if (newRemarks === null) return

    //     const fd = new FormData()
    //     fd.append('remarks', newRemarks)
    //     await axios.put(`${this.$apiURL}/api/vouchers/${voucherID}`, fd, { headers })
    //     this.toast.success('Voucher updated successfully')
    //   } catch (err) {
    //     console.error('❌ handleEditVoucher error:', err)
    //     this.toast.error('Failed to update voucher')
    //   }
    // },

    async deleteVoucher(voucherID) {
      // if (!confirm('Confirm delete this voucher?')) return
      try {
        const headers = await this.getAuthHeader()
        await axios.delete(`${this.$apiURL}/api/vouchers/${voucherID}`, { headers })
        this.toast.success('Voucher deleted successfully')
        await this.fetchBooking()
      } catch (err) {
        console.error('❌ deleteVoucher error:', err)
        this.toast.error('Failed to delete voucher')
      }
    },


    async saveAll() {
      if (!this.booking) return
      this.loadingSave = true
      try {
        const headers = await this.getAuthHeader()
        const bookingId = this.booking.id

        // 🟢 If Client: JSON payload + FormData for uploads
        if (this.isClient) {
          const fd = new FormData()

          let startTime = ''
          
          const safeStartDate = this.normalizeDateToInput(this.form.startDate)
          const safeStartTime = this.normalizeTimeToInput(this.form.startTime)

          if (safeStartDate && safeStartTime) {
            startTime = `${safeStartDate}T${safeStartTime}:00+08:00`
          }

          fd.append('startTime', startTime)
          fd.append('startLocation', this.form.startLocation || '')
          fd.append('endLocation', this.form.endLocation || '')
          fd.append('pax', this.form.pax || '')
          fd.append('vesselName', this.form.vesselName || '')
          fd.append('counterNumber', this.form.counterNumber || '')
          fd.append('pocContact', JSON.stringify(this.form.pocContact || []))
          fd.append('remarks', this.form.remarks || '')
          fd.append('crewName', this.form.crewName || '')
          fd.append(
            'flightNumbers',
            JSON.stringify(this.form.flightNumbers.filter(f => f.flight.trim() !== ''))
          )
          fd.append(
            'trips',
            JSON.stringify(this.form.trips.filter(t => t.trim() !== ''))
          )

          // 📎 Add uploaded files
          this.uploadedFiles.forEach((f, i) => {
            if (f.file) fd.append(`file_${i}`, f.file)
          })

          // ✅ Call /api/bookings/<id> (client route)
          await axios.put(`${this.$apiURL}/api/bookings/${bookingId}`, fd, { headers })
        }

        // 🟢 Admin/Driver: use /status endpoint
        if (!this.isClient) {
          const fd = new FormData()
          let finalStatus = this.form.status
          if (finalStatus === 'Completed') finalStatus = 'DOP'
          else if (finalStatus === 'Failure' || finalStatus === 'Cancelled') finalStatus = 'Failure'
          fd.append('status', finalStatus)
          if (this.form.remarks) fd.append('remark', this.form.remarks)
          if (this.form.crewName)fd.append('crewName', this.form.crewName || '')

          if (this.isAdmin) fd.append('driverIDs', JSON.stringify(this.form.driverIDs || []))
          this.uploadedFiles.forEach((f, i) => { if (f.file) fd.append(`file_${i}`, f.file) })
          await axios.put(`${this.$apiURL}/api/bookings/${bookingId}/status`, fd, { headers })
        }

        this.toast.success('Booking updated successfully')
        await this.fetchBooking()
      } catch (err) {
        console.error('❌ saveAll error:', err)
        this.toast.error('Error saving booking')
      } finally {
        this.loadingSave = false
      }
    },
    async fetchClients() {
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.get(`${this.$apiURL}/clients`, { headers })
        this.clients = res.data?.clients || []
      } catch (err) {
        console.error('❌ fetchClients error:', err)
        this.clients = []
      }
    },
    getClientName(clientId) {
      console.log(clientId)
      if (!clientId) return '—'
      if (typeof clientId === 'string' && clientId.toLowerCase() === 'admin') {
        return 'Admin'
      }
      const c = this.clients.find(cl => cl.id === clientId)
      return c ? (c.contactPerson || c.companyName || '—') : '—'
    },
    async verifyAccessToken(token) {
      try {
        const res = await axios.get(`${this.$apiURL}/api/token/verify`, {
          params: { token }
        })

        if (res.data && res.data.ok === true) {
          this.role = res.data.role
          localStorage.setItem('userRole', this.role)
          localStorage.setItem('accessToken', token)
          this.toast.success('Access verified as ' + this.role)
          return true
        } else {
          this.toast.error('Invalid or expired access token')
          return false
        }

      } catch (err) {
        console.error('❌ verifyAccessToken error:', err)
        this.toast.error('Access verification failed')
        return false
      }
    }




  },
  async mounted() {
    try {
      this.loading = true

      // 🔹 Get token from URL
      const tokenParam = this.$route.params.token

      if (tokenParam) {
        // ✅ Verify backend token and auto-set role
        await this.verifyAccessToken(tokenParam)
      } else {
        // 🚫 No token — block access
        this.toast.error('Access token missing or invalid')
        return
      }

      // ✅ Once verified, load booking and references
      await Promise.all([
        this.fetchBooking(),
        this.fetchDrivers(),
        this.fetchClients()
      ])

    } catch (err) {
      console.error('❌ mounted() error:', err)
      this.toast.error('Failed to initialize page')
    } finally {
      this.loading = false
    }
  }

}
</script>

<style scoped>
.form-control, .form-select { font-size: 0.95rem; }
.btn { min-height: 42px; }

.vue-timepicker-wrapper {
  width: 100%;
}

/* make its internal input match Bootstrap form-control */
:deep(.vue__time-picker input) {
  display: block;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

:deep(.vue__time-picker input:focus) {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13,110,253,0.25);
}
:deep(.vue__time-picker input:disabled) {
  background-color: #f8f9fa !important; /* slightly off-white */
  color: #212529 !important;            /* dark grey text */
  opacity: 1 !important;                /* prevent light grey fade */
  cursor: not-allowed;
}
</style>
